// Course Package Import — Step 1 reader.
// Browser-only. Unzips a .course.zip, locates the course.json index,
// builds a path -> bytes map and lists ignored files. No network, no saving.
// See /app/memory/course_package/PRD_course_package_import.md sections 2, 3, 9.1.

import { unzipSync, strFromU8 } from 'fflate';

export const MAX_ZIP_BYTES = 200 * 1024 * 1024; // C9: 200 MB whole-zip cap
export const MAX_FILE_BYTES = 50 * 1024 * 1024; // C9: 50 MB per file

// Files that are present for humans / the OS but never uploaded.
const IGNORED_EXACT = new Set(['.DS_Store', 'Thumbs.db']);
const isIgnoredPath = (path) => {
  const name = path.split('/').pop() || '';
  if (path.startsWith('__MACOSX/') || path.includes('/__MACOSX/')) return true;
  if (IGNORED_EXACT.has(name)) return true;
  if (/_HERE\.txt$/i.test(name)) return true; // template placeholder notes
  return false;
};

// Strip an optional single top-level wrapper folder so `folder/course.json`
// behaves exactly like a root `course.json`.
function resolveRoot(entries) {
  if (Object.prototype.hasOwnProperty.call(entries, 'course.json')) return '';
  const topLevel = new Set();
  for (const p of Object.keys(entries)) {
    const seg = p.split('/')[0];
    if (seg) topLevel.add(seg);
  }
  if (topLevel.size === 1) {
    const only = [...topLevel][0];
    if (Object.prototype.hasOwnProperty.call(entries, `${only}/course.json`)) {
      return `${only}/`;
    }
  }
  return null; // no index found
}

// Parse JSON and, on failure, return a message with line/column (section 9.3).
function parseJsonWithPosition(text) {
  try {
    return { value: JSON.parse(text) };
  } catch (err) {
    let line = null;
    let column = null;
    const m = /position (\d+)/.exec(err.message || '');
    if (m) {
      const pos = Number(m[1]);
      const upto = text.slice(0, pos);
      line = upto.split('\n').length;
      column = pos - upto.lastIndexOf('\n');
    }
    return { error: err.message, line, column };
  }
}

/**
 * Read a course package File.
 * @returns {Promise<ReadResult>}
 *   ReadResult = {
 *     ok: boolean,
 *     error?: { code, message },
 *     packageName: string,
 *     index?: object,            // parsed course.json
 *     files: Map<string, Uint8Array>, // relative path (root stripped) -> bytes
 *     ignored: string[],         // relative paths listed but never uploaded
 *     oversizeFiles: string[],   // relative paths over 50 MB
 *     totalBytes: number,
 *   }
 */
export async function readPackage(file) {
  const base = {
    ok: false,
    packageName: file ? file.name : '',
    files: new Map(),
    ignored: [],
    oversizeFiles: [],
    totalBytes: 0,
  };

  if (!file) return { ...base, error: { code: 'PKG_NO_FILE', message: 'No file selected.' } };
  if (!/\.zip$/i.test(file.name)) {
    return { ...base, error: { code: 'FILE_TYPE', message: 'Choose a .zip course package.' } };
  }
  if (file.size > MAX_ZIP_BYTES) {
    return { ...base, error: { code: 'PKG_TOO_BIG', message: 'The zip is over 200 MB.' } };
  }

  let entries;
  try {
    const buf = new Uint8Array(await file.arrayBuffer());
    entries = unzipSync(buf);
  } catch (err) {
    return { ...base, error: { code: 'PKG_BAD_ZIP', message: `Could not read the zip: ${err.message}` } };
  }

  const root = resolveRoot(entries);
  if (root === null) {
    return { ...base, error: { code: 'PKG_NO_INDEX', message: 'course.json not found at the zip root.' } };
  }

  const files = new Map();
  const ignored = [];
  const oversizeFiles = [];
  let totalBytes = 0;
  for (const [rawPath, bytes] of Object.entries(entries)) {
    if (rawPath.endsWith('/')) continue; // directory entry
    if (root && !rawPath.startsWith(root)) continue;
    const rel = root ? rawPath.slice(root.length) : rawPath;
    if (!rel) continue;
    if (isIgnoredPath(rel)) {
      ignored.push(rel);
      continue;
    }
    totalBytes += bytes.length;
    if (bytes.length > MAX_FILE_BYTES) oversizeFiles.push(rel);
    files.set(rel, bytes);
  }

  const indexBytes = files.get('course.json');
  const parsed = parseJsonWithPosition(strFromU8(indexBytes));
  if (parsed.error) {
    const pos = parsed.line ? ` (line ${parsed.line}, column ${parsed.column})` : '';
    return {
      ...base,
      files,
      ignored,
      oversizeFiles,
      totalBytes,
      error: { code: 'PKG_BAD_JSON', message: `course.json is not valid JSON${pos}.` },
    };
  }

  return {
    ok: true,
    packageName: file.name,
    index: parsed.value,
    files,
    ignored,
    oversizeFiles,
    totalBytes,
  };
}

export function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
